import React from 'react';
import { Badge } from '@cowbell/ui';
import type { Agent } from '../types';
import ClaimsAgent from '../../../_assets/svg/agents/ClaimsAgent.svg';
import ConciergeAgent from '../../../_assets/svg/agents/ConciergeAgent.svg';
import CyberPulseAgent from '../../../_assets/svg/agents/CyberPulseAgent.svg';
import CyberUWAgent from '../../../_assets/svg/agents/CyberUWAgent.svg';
import ENOUWAgent from '../../../_assets/svg/agents/ENOUWAgent.svg';
import MLUWAgent from '../../../_assets/svg/agents/MLUWAgent.svg';
import MooAgent from '../../../_assets/svg/agents/MooAgent.svg';
import PulseAgent from '../../../_assets/svg/agents/PulseAgent.svg';
import REAgent from '../../../_assets/svg/agents/REAgent.svg';
import RenewalsAgent from '../../../_assets/svg/agents/RenewalsAgent.svg';
import SubmissionAgent from '../../../_assets/svg/agents/SubmissionAgent.svg';
import type { AgentIconProps } from '../../../_assets/svg/agents/AgentIconBase';
import styles from './AgentAvatar.module.css';
import { AVATAR_SIZE_REM, type AvatarSize } from './agent-tile-size';
import type { StudioAvatarCategory } from '@cowbell/moo';
import { DEFAULT_MOO_ID } from '../v2/agent-order';

/** The category the Moo drawer passes for its Studio link. Typed against the
 *  library's own value, so a change there fails type-check here; a type-only
 *  import keeps the chat library's runtime out of this component. */
const STUDIO_AVATAR_CATEGORY: StudioAvatarCategory = 'studio';

/** Initials take this share of the tile edge. */
const INITIALS_SCALE = 0.4;

type AgentIconComponent = React.ComponentType<AgentIconProps>;

const AGENT_ICON_MAP: Record<string, AgentIconComponent> = {
  claimsense: ClaimsAgent,
  claims: ClaimsAgent,
  concierge: ConciergeAgent,
  cyber: CyberUWAgent,
  shield: CyberPulseAgent, // cyber-pulse agent (agent.yaml icon: shield) — OMNI-435

  'eno-uw': ENOUWAgent,
  'tech-eo': ENOUWAgent,
  'management-liability': MLUWAgent,
  'chart-bar': PulseAgent,
  // Icon key, not an agent id: risk-engineering-agent and risk-insights-advisor
  // both declare `icon: risk-advisor`.
  'risk-advisor': REAgent,
  renewals: RenewalsAgent, // renewals agent (agent.yaml icon: renewals) — OMNI-751
  submissions: SubmissionAgent,
  moo: MooAgent,
};

function getInitials(name: string): string {
  const words = name.trim().split(/\s+/);
  if (words.length < 2) return (words[0]?.[0] ?? 'A').toUpperCase();
  return (words[0][0] + words[1][0]).toUpperCase();
}

interface AgentAvatarProps {
  /** An agent, or the Moo drawer's Studio entry (category 'studio'). */
  readonly agent?:
    | (Pick<Agent, 'id' | 'name' | 'subtitle' | 'icon' | 'color'> & {
        readonly category?: Agent['category'] | StudioAvatarCategory;
      })
    | null;
  readonly size?: AvatarSize;
  readonly showBadge?: boolean;
  readonly className?: string;
}

function AgentTile({
  size,
  inverse = false,
  children,
}: Readonly<{
  size: AvatarSize;
  inverse?: boolean;
  children: React.ReactNode;
}>) {
  const edge = `${AVATAR_SIZE_REM[size]}rem`;
  return (
    <div
      className={inverse ? `${styles.tile} ${styles.inverse}` : styles.tile}
      style={{ width: edge, height: edge }}
      data-testid="agent-tile"
    >
      {children}
    </div>
  );
}

function Initials({
  name,
  size,
}: Readonly<{ name: string; size: AvatarSize }>) {
  return (
    <span
      className={styles.initials}
      style={{ fontSize: `${AVATAR_SIZE_REM[size] * INITIALS_SCALE}rem` }}
      data-testid="agent-initials"
    >
      {getInitials(name)}
    </span>
  );
}

/**
 * An agent's face: its glyph in white on a Cowbell-navy tile, on the
 * platform corner radius. Custom agents, and any agent whose icon key has no
 * glyph, show their initials the same way.
 */
export default function AgentAvatar({
  agent,
  size = 'md',
  showBadge = false,
  className,
}: AgentAvatarProps) {
  const isMoo = !agent || agent.id === DEFAULT_MOO_ID;
  const AgentIcon =
    agent && agent.category !== 'custom' && agent.icon
      ? AGENT_ICON_MAP[agent.icon]
      : undefined;

  let tile: React.ReactNode;
  if (agent?.category === STUDIO_AVATAR_CATEGORY) {
    // Moo Studio, not an agent: the Moo mark flipped, blue on white, so the
    // Studio link stands apart from the agent tiles listed under it.
    tile = (
      <AgentTile size={size} inverse>
        <MooAgent className={styles.glyph} />
      </AgentTile>
    );
  } else if (isMoo) {
    tile = (
      <AgentTile size={size}>
        <MooAgent className={styles.glyph} />
      </AgentTile>
    );
  } else if (AgentIcon) {
    tile = (
      <AgentTile size={size}>
        <AgentIcon className={styles.glyph} />
      </AgentTile>
    );
  } else {
    tile = (
      <AgentTile size={size}>
        <Initials name={agent.name || 'Agent'} size={size} />
      </AgentTile>
    );
  }

  return (
    <div className={`${styles.wrapper} ${className || ''}`}>
      {tile}
      {showBadge && (
        <Badge className={styles.badge} size="xs" circle color="green" />
      )}
    </div>
  );
}
